import { z } from "zod";
import { GroupId, StudentGroupCard } from "./groups.ts";
import {
  ExternalCallUrl,
  ExternalCallUrlView,
  LESSON_DURATION_MAX,
  LESSON_DURATION_MIN,
  LocalDate,
  LocalTime,
  SERIES_SLOT_MAX,
  ScheduleSlotInput,
  SeriesId,
  SlotId,
  TimeZoneName,
  Weekday,
  localDateAt,
} from "./schedule.ts";
import { UserId } from "./user.ts";

/**
 * Расписание ГРУППЫ: контракт пучка серий.
 *
 * Проект целиком в `docs/group-lessons-design.md`, это его раздел 3. Решение,
 * из которого выведена вся форма ниже, стоит в разделе 6 CLAUDE.md и менять его
 * нельзя:
 *
 * > **группа это пучок пар, а не новая сущность**
 *
 * То есть занятие группы это N обычных серий с общим `group_id`, по одной на
 * пару, а не одна серия с N учениками. Схема к этому принуждает
 * (`schedule_series.student_user_id` объявлен `NOT NULL`), но важнее следствие
 * для контракта: **всё, что висит на паре, здесь не появляется вовсе.** Ни
 * отметки занятия (её ключ это `(slot_id, occurs_on)` ОДНОЙ пары), ни домашки,
 * ни оплаты, ни архива. Пучок это только расписание.
 *
 * ⚠️ **Ученику из этого файла не уезжает ни одно поле, и это не наблюдение о
 * сегодняшних ручках, а требование к завтрашним.** В расписании ученика занятие
 * группы это его занятие, и признаков группы там нет: ни названия, ни числа
 * участников, ни `groupId`. Все ручки пучка адресованы преподавателю-владельцу
 * группы, предикат у них один и тот же (`teacher_user_id = :я` на строке
 * группы), и ученик получает 404 ровно как посторонний. Понадобится ученику
 * что-то знать о группе, это будет отдельная ручка с отдельным правилом
 * видимости, а не ослабленный предикат в этой.
 *
 * ⚠️ **Адрес пучка это САМА ГРУППА.** Своего идентификатора у него нет и
 * заводить его не надо: у группы одно действующее расписание, а «разное время в
 * разные дни» выражается несколькими СЛОТАМИ внутри пучка, ровно как у обычной
 * серии. Отдельное занятие пучка адресуется парой `(slotId, occursOn)`, где
 * `slotId` это ПРЕДСТАВИТЕЛЬ: у каждого участника свой слот, сервер разрешает
 * представителя в остальные по совпадению дня недели и времени. Второго адреса
 * у занятия нет, и это то же правило, что во всём расписании
 * (`docs/schedule-design.md`, 5.4).
 */

/**
 * Длительность занятия. Своя копия правила, потому что в `./schedule.ts` эта
 * схема приватная, а числа общие и живут константами.
 */
const DurationMin = z.number().int().min(LESSON_DURATION_MIN).max(LESSON_DURATION_MAX);

/* ─────────────────────────── что уезжает наружу ──────────────────────────── */

/**
 * Слот пучка: один день недели и время, одинаковые у всех участников.
 *
 * ⚠️ `slotId` это ПРЕДСТАВИТЕЛЬ, а не единственный слот. Настоящих слотов
 * столько же, сколько участников, и все они лежат в своих сериях; наружу едет
 * один, и он же адресует занятие в ручках отмены и переноса. Выбирается
 * представитель детерминированно (наименьший идентификатор среди слотов пучка с
 * этим днём и временем), поэтому адрес не «прыгает» между запросами и совпадает
 * с тем, который приезжает строкой сетки.
 */
export const GroupScheduleSlotView = z.object({
  slotId: SlotId,
  weekday: Weekday,
  startsAt: LocalTime,
  /** `null` означает «как у пучка». */
  durationMin: z.number().int().nullable(),
  startsOn: LocalDate.nullable(),
  endsOn: LocalDate.nullable(),
});
export type GroupScheduleSlotView = z.infer<typeof GroupScheduleSlotView>;

/**
 * Участник пучка: чья это серия и с какого числа он в занятиях.
 *
 * `startsOn` у всех разный ровно в одном случае, и он штатный: человека
 * добавили в группу в середине четверти, и он получает свою серию **с
 * ближайшего занятия**, а не задним числом (раздел 3 проекта). В прошедшие
 * занятия он не добавляется: у него нет там ни урока, ни отметки, и заводить их
 * задним числом не за что.
 *
 * ⚠️ Имени здесь нет намеренно. Это преподавательская форма, но профильное имя
 * ученика в ней означало бы, что список участников пучка и состав группы
 * отвечают на один вопрос двумя разными именами. Имена берутся из состава
 * группы (`GET /v1/groups/:groupId`), где у них один читатель и одно хранилище.
 *
 * ⚠️ **`unlinked` означает «серия есть, а занятий по ней не производится ни
 * одного»** (заведено по итогам общего ревью 30.08.2026). Отвязка пары
 * (`unlinked_at`) из группы не выкидывает, это решение раздела 3 проекта:
 * ссылка ученика перестаёт работать и новых договорённостей по паре не завести,
 * но состав и серия остаются на месте. Занятия при этом обрываются датой
 * отвязки (`expandSeries`), поэтому человек едет в `members` с датой начала и не
 * приходит никуда. Без признака экран расписания группы показывал его наравне с
 * остальными, то есть ровно то молчаливое выпадение, ради запрета которого
 * заведён `skipped` (5.5 CLAUDE.md). Возвращается всё перевыпуском ссылки, и
 * тогда же пару догоняют исключения пучка
 * (`repo/schedule-bunch-exceptions.ts`).
 *
 * ⚠️ **Отсюда два числа, и они отвечают на РАЗНЫЕ вопросы.** `members.length`
 * это «сколько человек в расписании группы», считая отвязанных: они в составе и
 * вернутся одним нажатием. `ScheduleEntryGroup.size` в строке сетки это
 * «сколько пар производит ЭТО занятие», то есть отвязанных там нет. Совпадать
 * они не обязаны, и третьего числа взамен клиенту заводить не надо: расхождение
 * между ними и есть список отвязанных.
 */
export const GroupScheduleMemberView = z.object({
  studentUserId: UserId,
  seriesId: SeriesId,
  startsOn: LocalDate,
  endsOn: LocalDate.nullable(),
  unlinked: z.boolean(),
});
export type GroupScheduleMemberView = z.infer<typeof GroupScheduleMemberView>;

/**
 * Кого в пучок не взяли и почему.
 *
 * ⚠️ **Из группового расписания никто не выпадает молча**, и это то же правило,
 * что уже стоит у рассылки: преподаватель, заведший занятие для группы на
 * десять человек, обязан узнать, что серий получилось восемь, и кому именно не
 * досталось. Молчаливая деградация запрещена 5.5 CLAUDE.md.
 *
 * Причин две, и приходят они с разных путей:
 *
 * - `student_not_linked`: пара отвязана, а отвязанная пара новых
 *   договорённостей не заводит (`requireActivePair`, раздел 9
 *   `docs/schedule-design.md`). Из группы отвязка при этом не выкидывает
 *   (раздел 3 проекта), поэтому такой человек остаётся в составе и просто не
 *   получает серии. Приезжает при заведении расписания;
 * - `no_upcoming_occurrence`: расписание у группы есть и оно действующее, но
 *   ближайшего занятия по нему не нашлось на год вперёд, значит догнавшему
 *   нечего дать. Приезжает при ДОБАВЛЕНИИ участника, см.
 *   `AddGroupMemberResponse`.
 */
export const GroupScheduleSkipView = z.object({
  studentUserId: UserId,
  reason: z.enum(["student_not_linked", "no_upcoming_occurrence"]),
});
export type GroupScheduleSkipView = z.infer<typeof GroupScheduleSkipView>;

/**
 * Ответ на добавление участника в группу: карточка плюс судьба его расписания.
 *
 * ⚠️ **Форма живёт ЗДЕСЬ, а не рядом с `StudentGroupCard`**, и это не оплошность
 * раскладки: `./schedule-groups.ts` уже импортирует `./groups.ts`, а обратный
 * импорт замкнул бы цикл модулей, то есть `ReferenceError` на инициализации
 * zod-схемы в мёртвой зоне (та же ловушка описана у `attendance` в
 * `./schedule.ts`).
 *
 * ⚠️ **Поле необязательное, и отсутствие означает «серия заведена либо не была
 * нужна».** Добавление в группу без расписания это нормальный случай
 * (рассылочный список), и говорить о нём нечего. Говорить есть о чём ровно
 * тогда, когда расписание есть, а серии человек не получил: до правки по итогам
 * ревью 29.08.2026 такой ученик числился в составе, а в расписании его не было,
 * и не узнавал об этом никто. Молчаливая деградация запрещена 5.5 CLAUDE.md.
 */
export const AddGroupMemberResponse = StudentGroupCard.extend({
  scheduleSkipped: GroupScheduleSkipView.optional(),
});
export type AddGroupMemberResponse = z.infer<typeof AddGroupMemberResponse>;

/**
 * Расписание группы целиком: пучок как одна вещь.
 *
 * ⚠️ `startsOn` и `endsOn` наверху это границы ПУЧКА, а не какой-то одной
 * серии: первое это самая ранняя дата среди участников, второе это самая
 * поздняя, а `null` в нём означает «хоть у одного бессрочно», то есть
 * «бессрочно». Личные границы догнавших и ушедших лежат в `members`, и путать
 * их нельзя: наверху ответ на вопрос «когда у группы занятия», внизу ответ на
 * вопрос «а у этого с какого числа».
 *
 * Считается это правило одной функцией `bunchBounds` внизу файла, и зовут её
 * оба: сервер при сборке этого ответа и список серий на клиенте.
 */
export const GroupScheduleView = z.object({
  groupId: GroupId,
  groupTitle: z.string(),
  timeZone: TimeZoneName,
  startsOn: LocalDate,
  endsOn: LocalDate.nullable(),
  durationMin: z.number().int(),
  lessonTitle: z.string().nullable(),
  defaultPlanId: z.string().uuid().nullable(),
  /** Название заготовки, чтобы экран не ходил за ним вторым запросом. */
  defaultPlanTitle: z.string().nullable(),
  /**
   * Внешний созвон. ⚠️ У группового занятия он не «запасной путь», а
   * сегодняшний единственный: своей комнаты у пучка пока нет (пункт 3 порядка
   * работ раздела 10 проекта), и до неё занятие группы идёт в Zoom или
   * Телемосте. Режим сосуществования и так объявлен штатной конфигурацией
   * продукта (раздел 2 CLAUDE.md).
   */
  externalCallUrl: ExternalCallUrlView.nullable(),
  slots: z.array(GroupScheduleSlotView),
  members: z.array(GroupScheduleMemberView),
  skipped: z.array(GroupScheduleSkipView),
});
export type GroupScheduleView = z.infer<typeof GroupScheduleView>;

/* ─────────────────────────── что приезжает внутрь ────────────────────────── */

/**
 * Завести расписание для группы: сервер создаёт N серий разом.
 *
 * ⚠️ `studentUserId` здесь НЕТ, и это главное отличие от `CreateSeriesRequest`:
 * состав берётся из группы, а не из тела запроса. Иначе появился бы второй
 * ответ на вопрос «кто в группе», и он бы разошёлся с первым в тот же день,
 * когда состав поменяли.
 *
 * ⚠️ Приватной заметки (`note` у обычной серии) здесь нет намеренно: заметка
 * это то, что пишут ПРО УЧЕНИКА, а пучок это не ученик. Одна заметка,
 * размноженная в шесть серий, стала бы шестью копиями, которые никто не правит
 * вместе.
 */
export const CreateGroupScheduleRequest = z.object({
  /**
   * Зона ПУЧКА, одна на всех. Как у обычной серии, копия делает выбор явным:
   * преподаватель, переехавший в другой город, сам решает, что делать с уже
   * согласованными занятиями.
   */
  timeZone: TimeZoneName,
  startsOn: LocalDate,
  endsOn: LocalDate.nullable().optional(),
  durationMin: DurationMin.optional(),
  lessonTitle: z.string().min(1).max(200).nullable().optional(),
  defaultPlanId: z.string().uuid().nullable().optional(),
  externalCallUrl: ExternalCallUrl.nullable().optional(),
  slots: z.array(ScheduleSlotInput).min(1).max(SERIES_SLOT_MAX),
});
export type CreateGroupScheduleRequest = z.infer<typeof CreateGroupScheduleRequest>;

/**
 * Правка пучка целиком. Отсутствие поля означает «не трогать».
 *
 * ⚠️ `startsOn` сюда НЕ входит, и это не пропуск. Начало у каждого участника
 * своё: догнавший в середине четверти получил свою дату, и общий сдвиг начала
 * либо стёр бы её, либо задним числом добавил бы человека в занятия, которых у
 * него не было. Двигать надо слоты (`PATCH .../slots/:slotId`), а не начало
 * пучка.
 *
 * ⚠️ Слоты сюда тоже не входят: у них свои ручки, потому что правка слота это
 * три проверки и подтверждение осиротевших уроков, а не присваивание поля.
 */
export const UpdateGroupScheduleRequest = z.object({
  timeZone: TimeZoneName.optional(),
  endsOn: LocalDate.nullable().optional(),
  durationMin: DurationMin.optional(),
  lessonTitle: z.string().min(1).max(200).nullable().optional(),
  defaultPlanId: z.string().uuid().nullable().optional(),
  externalCallUrl: ExternalCallUrl.nullable().optional(),
});
export type UpdateGroupScheduleRequest = z.infer<typeof UpdateGroupScheduleRequest>;

/**
 * Ответ отмены и переноса занятия группы.
 *
 * Одна строка, а не N: у всех участников исключение одно и то же по значению
 * (вид, новое место, причина), а различаются они только своим `slot_id`,
 * который снаружи не адресуем. Поэтому наружу едет представитель плюс число
 * задетых пар: оно и есть подтверждение того, что операция была атомарной, а не
 * половинчатой.
 */
export const GroupOccurrenceView = z.object({
  slotId: SlotId,
  /** ИСХОДНАЯ дата занятия. При переносе не меняется никогда. */
  occursOn: LocalDate,
  kind: z.enum(["cancelled", "moved"]),
  movedToDate: LocalDate.nullable(),
  movedToTime: LocalTime.nullable(),
  reason: z.string().nullable(),
  /** Сколько пар пучка задето. Ноль невозможен: тогда это отказ. */
  affected: z.number().int().positive(),
});
export type GroupOccurrenceView = z.infer<typeof GroupOccurrenceView>;

/* ─────────────────── правила, общие клиенту и серверу ────────────────────── */

/**
 * Серия и её зона: минимальная форма правила «ещё производит занятия».
 *
 * Зона обязательна и выкинуть её нельзя: «сегодня» у сервера в UTC и у
 * преподавателя во Владивостоке это разные даты, а дата конца сравнивается с
 * датой В ЗОНЕ СЕРИИ. Форма подходит `ScheduleSeriesView` как есть, а строка
 * базы перекладывается на границе (`seriesActive` в
 * `apps/api/src/schedule-group-bunch.ts`), как и у `bunchBounds` ниже.
 */
export interface SeriesLifetime {
  endsOn: string | null;
  timeZone: string;
}

/**
 * Производит ли серия занятия в этот момент и дальше.
 *
 * ⚠️ **Переехало сюда из `apps/api` по итогам ревью 30.08.2026, и довод тот же,
 * что у `bunchBounds` ниже: читателей у правила два, и второй это клиент.**
 * Границы пучка считаются по ДЕЙСТВУЮЩИМ сериям, и сервер кормит `bunchBounds`
 * уже отобранным набором (`activeSeries` в `repo/schedule-groups.ts`), а клиент
 * получает из `GET /v1/schedule/series` ВСЕ серии подряд, включая закрытые
 * (серии пучка из ответа не убираются намеренно, раздел 3 проекта, пункт 3).
 * Пока отбор лежал только на сервере, общей у двоих была арифметика, но не
 * входной набор, то есть переехала половина правила.
 *
 * Расходятся они в начале пучка и молча: у ушедшего участника `starts_on` при
 * закрытии серии не двигается, поэтому сервер сказал бы «со 2 ноября», а клиент
 * по всем сериям «с 1 сентября».
 *
 * ⚠️ Признак это `endsOn`, а не отдельное состояние: «завершить серию» во всём
 * проекте выражается датой окончания (5.6 `docs/schedule-design.md`), и второго
 * способа заводить не надо. Дата ВКЛЮЧИТЕЛЬНАЯ: в этот день занятие ещё есть.
 *
 * ⚠️ Даты сравниваются как строки, и законно это ровно для формы `YYYY-MM-DD`,
 * разбор у `BunchPeriod` ниже.
 */
export function seriesActiveAt(series: SeriesLifetime, now: number): boolean {
  return series.endsOn === null || series.endsOn >= localDateAt(now, series.timeZone);
}

/**
 * Период занятий: с какого числа и по какое. `null` в конце это «бессрочно».
 *
 * Форма нарочно минимальная, две даты и ничего больше. Звателей у правила ниже
 * двое, и структуры у них разные: на сервере это строка базы (`starts_on`,
 * `ends_on`), на клиенте `ScheduleSeriesView` (`startsOn`, `endsOn`). Общий
 * пакет не должен знать ни ту, ни другую, иначе он перестаёт быть общим:
 * сервер перекладывает две даты, клиент отдаёт свои поля как есть.
 *
 * ⚠️ Дата это строка `YYYY-MM-DD`, и сравнивается она КАК СТРОКА. Законно
 * ровно для этой формы: она с ведущими нулями и в порядке убывания
 * старшинства, поэтому лексикографический порядок совпадает с календарным.
 * `Date` был бы хуже, а не строже: он втащил бы зону процесса в правило,
 * которое про календарь, и «сегодня» у сервера в UTC разошлось бы с «сегодня»
 * у преподавателя.
 */
export interface BunchPeriod {
  startsOn: string;
  endsOn: string | null;
}

/**
 * Границы ПУЧКА по границам его участников.
 *
 * Правило целиком: начало это самая ранняя дата среди участников, конец это
 * самая поздняя, а `null` в конце хоть у одного означает «бессрочно» у всего
 * пучка. Последнее не симметрично первому и симметричным быть не может:
 * бессрочная серия производит занятия и после любой конечной даты, поэтому
 * пучок с бессрочным участником заканчиваться не может вовсе.
 *
 * ⚠️ **Функция живёт здесь, а не на сервере, потому что читателей у правила
 * два.** Список серий на клиенте сворачивает пучок в одну строку сам (раздел 3
 * `docs/group-lessons-design.md`, пункт 3: отдельная ручка отвергнута, она
 * звала бы `planTitles` на каждую группу). Вторая копия правила разошлась бы с
 * первой молча: расхождение в дате конца не падает и не логируется, его видно
 * только глазами и только тому, у кого пучок собран из срочных и бессрочных
 * серий сразу.
 *
 * ⚠️ **На вход идут только ДЕЙСТВУЮЩИЕ серии, и отбирает их `seriesActiveAt`
 * выше.** Правило это часть того же ответа: закрытая серия ушедшего участника
 * несёт своё старое начало, и попав в набор, она сдвинула бы начало пучка на
 * дату, когда у сегодняшнего состава занятий ещё не было. Отбор здесь не
 * делается сам, потому что функции неоткуда взять момент времени, а «сейчас» у
 * серии считается в её собственной зоне.
 *
 * ⚠️ **Пустой список это `null`, а не пучок с пустыми границами.** Соблазн
 * вернуть `{ startsOn: ..., endsOn: null }` надо гасить: `null` в конце уже
 * занят смыслом «бессрочно», и пучок без участников притворился бы бессрочной
 * группой, то есть самым живым, что бывает. Отвечать за пустой список должен
 * зватель, и у обоих ответ уже есть: сервер отказывает `series_not_found`
 * раньше (`baseSeries`), а клиент строит секцию из непустых групп по
 * построению.
 */
export function bunchBounds(members: readonly BunchPeriod[]): BunchPeriod | null {
  let startsOn: string | null = null;
  let endsOn: string | null = null;
  // Флаг отдельно от `endsOn`, потому что `null` в нём означает две разные
  // вещи: «конца нет» и «ещё не встретили ни одной даты»
  let endless = false;
  for (const m of members) {
    if (startsOn === null || m.startsOn < startsOn) startsOn = m.startsOn;
    if (m.endsOn === null) endless = true;
    else if (endsOn === null || m.endsOn > endsOn) endsOn = m.endsOn;
  }
  if (startsOn === null) return null;
  return { startsOn, endsOn: endless ? null : endsOn };
}
